import { IconCheck, IconCopy, IconFileText } from "@tabler/icons-react";
import { Duration } from "luxon";
import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { twMerge } from "tailwind-merge";
import { ChatScrollState, useChatScroll } from "../chat/scroll.js";
import { useCopyAction } from "../clipboard/useCopyAction.js";
import { capitalize } from "../helpers/helpers.js";
import { FloatingAutoScrollResumeButton } from "../transcript/autoScrollResumeButton.js";

export interface Utterance {
  role: "agent" | "customer";
  text: string;
  startTimeSeconds: number;
  endTimeSeconds: number;
  highlights: UtteranceHighlight[];
}

export interface UtteranceHighlight {
  startIndex: number;
  endIndex: number;
  text: string;
}

export interface TranscriptContentProps {
  isAutoScrollActive?: boolean;
  utterances: readonly Utterance[];
}

export const TranscriptContent: FC<TranscriptContentProps> = (props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const [isInteracting, setIsInteracting] = useState(false);
  const [isAutoScrollSuspended, setIsAutoScrollSuspended] = useState(false);

  const { scrollToBottom } = useChatScroll(containerRef, contentRef, {
    isSuspended: isInteracting || isAutoScrollSuspended,
  });

  const resumeTimeoutRef = useRef<number | null>(null);

  const clearResumeTimeout = useCallback(() => {
    if (resumeTimeoutRef.current !== null) {
      window.clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }
  }, []);

  const beginInteraction = useCallback(() => {
    clearResumeTimeout();
    setIsInteracting(true);
  }, [clearResumeTimeout]);

  const endInteraction = useCallback(() => {
    clearResumeTimeout();
    resumeTimeoutRef.current = window.setTimeout(() => {
      resumeTimeoutRef.current = null;
      setIsInteracting(false);
    }, 1000);
  }, [clearResumeTimeout]);

  useEffect(() => clearResumeTimeout, [clearResumeTimeout]);

  useEffect(() => {
    if (isInteracting || isAutoScrollSuspended) return;
    scrollToBottom();
  }, [
    scrollToBottom,
    props.utterances.length,
    isInteracting,
    isAutoScrollSuspended,
  ]);

  useEffect(() => {
    if (!props.isAutoScrollActive) {
      setIsAutoScrollSuspended(false);
    }
  }, [props.isAutoScrollActive]);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    setIsAutoScrollSuspended(
      Boolean(props.isAutoScrollActive) &&
        !new ChatScrollState(event.currentTarget).isVisuallyScrolledToBottom,
    );
  };

  const resumeAutoScroll = () => {
    setIsAutoScrollSuspended(false);
    scrollToBottom();
  };

  const handleMouseOver = (event: React.MouseEvent<HTMLDivElement>) => {
    const interactive = (event.target as Element | null)?.closest(
      "[data-interactive='true']",
    );
    const fromInteractive = (event.relatedTarget as Element | null)?.closest?.(
      "[data-interactive='true']",
    );
    if (interactive && interactive !== fromInteractive) {
      beginInteraction();
    }
  };

  const handleMouseOut = (event: React.MouseEvent<HTMLDivElement>) => {
    const interactive = (event.target as Element | null)?.closest(
      "[data-interactive='true']",
    );
    const toInteractive = (event.relatedTarget as Element | null)?.closest?.(
      "[data-interactive='true']",
    );
    if (interactive && interactive !== toInteractive) {
      endInteraction();
    }
  };

  return (
    <div
      ref={containerRef}
      className="h-0 flex-auto overflow-y-auto px-4 py-3"
      onScroll={handleScroll}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
    >
      {props.utterances.length === 0 ? (
        <div
          ref={contentRef}
          className="flex flex-col items-center justify-center py-12 text-center"
        >
          <IconFileText className="mb-2 size-8 text-slate-300" />
          <p className="text-xs text-slate-400">
            Transcript will appear here when the call starts.
          </p>
        </div>
      ) : (
        <div ref={contentRef} className="space-y-3">
          {props.utterances.map((utterance) => {
            return (
              <div key={utterance.startTimeSeconds}>
                <div className="mb-0.5 flex items-center gap-2">
                  <span
                    className={twMerge(
                      "text-[10px] font-semibold",
                      utterance.role === "agent"
                        ? "text-indigo-500"
                        : "text-slate-400",
                    )}
                  >
                    {capitalize(utterance.role)}
                  </span>
                  <span className="text-[10px] text-slate-300 tabular-nums">
                    {Duration.fromObject({
                      seconds: utterance.startTimeSeconds,
                    }).toFormat("mm:ss")}
                  </span>
                </div>
                <p className="text-sm leading-relaxed text-foreground">
                  {renderUtteranceText(utterance.text, utterance.highlights)}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {isAutoScrollSuspended ? (
        <FloatingAutoScrollResumeButton onResume={resumeAutoScroll} />
      ) : null}
    </div>
  );
};

function renderUtteranceText(
  text: string,
  highlights: readonly UtteranceHighlight[],
): ReactNode {
  if (highlights.length === 0) {
    return text;
  }

  const sorted = [...highlights].sort((a, b) => a.startIndex - b.startIndex);

  const nodes: ReactNode[] = [];
  let cursor = 0;

  for (const highlight of sorted) {
    const start = Math.max(highlight.startIndex, cursor);
    const end = Math.min(highlight.endIndex, text.length);
    if (start >= end) {
      continue;
    }
    if (start > cursor) {
      nodes.push(text.slice(cursor, start));
    }
    nodes.push(
      <HighlightedText key={`${start}-${end}`} text={text.slice(start, end)} />,
    );
    cursor = end;
  }

  if (cursor < text.length) {
    nodes.push(text.slice(cursor));
  }

  return nodes;
}

const HighlightedText: FC<{ text: string }> = (props) => {
  const { didJustCopy, onCopy } = useCopyAction(props.text);

  return (
    <span
      onClick={onCopy}
      data-interactive="true"
      className="group/highlight relative inline-flex cursor-pointer items-center rounded-md bg-amber-100/70 px-1 font-medium text-amber-900 transition-colors hover:bg-amber-200/80"
    >
      {props.text}
      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center rounded-r-md opacity-0 transition-opacity group-hover/highlight:pointer-events-auto group-hover/highlight:opacity-100">
        <span className="h-full w-6 bg-gradient-to-r from-transparent to-amber-200/90" />
        <span className="flex h-full items-center rounded-r-md bg-amber-200/90 pr-1">
          {didJustCopy ? (
            <IconCheck className="size-3 text-emerald-600" />
          ) : (
            <IconCopy className="size-3 text-amber-700" />
          )}
        </span>
      </span>
    </span>
  );
};
